import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Dialog, TextInput } from "fe-shared/src/components";

import { MAX_COMMENT_LENGTH } from "../PickupRequestForm/modules/config";
import styles from "./InvalidationModal.module.scss";

export interface InvalidationPayload {
  comment: string;
  showToParticipants?: boolean;
}

export interface Props {
  isActive: boolean;
  showParticipantsOption?: boolean;
  requestNumber: string;
  onConfirm: (payload: InvalidationPayload) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const InvalidationModal = (props: Props) => {
  const {
    isActive,
    showParticipantsOption = true,
    requestNumber,
    onConfirm,
    onCancel,
    isSubmitting = false,
  } = props;
  const { t } = useTranslation();

  const [comment, setComment] = useState("");
  const [showToParticipants, setShowToParticipants] = useState(false);
  const [isReasonsMissing, setIsReasonsMissing] = useState(false);

  useEffect(() => {
    if (isActive) {
      setComment("");
      setShowToParticipants(false);
      setIsReasonsMissing(false);
    }
  }, [isActive]);

  const handleCommentChange = (_event: unknown, newValue: string) => {
    setComment(newValue);

    if (isReasonsMissing && newValue.trim()) {
      setIsReasonsMissing(false);
    }
  };

  const handleConfirm = () => {
    const trimmedComment = comment.trim();

    if (!trimmedComment) {
      setIsReasonsMissing(true);
      return;
    }

    onConfirm(
      showParticipantsOption
        ? { comment: trimmedComment, showToParticipants }
        : { comment: trimmedComment }
    );
  };

  return (
    <Dialog
      modal
      isActive={isActive}
      onClose={onCancel}
      title={
        <span>
          <span>{t("pickupRequests.invalidation.title")}</span>{" "}
          <span>{requestNumber}</span>
        </span>
      }
      actions={[
        <Button
          id="invalidation-modal-confirm"
          key="confirm"
          variant="filled"
          disabled={isSubmitting}
          onClick={handleConfirm}
        >
          {t("pickupRequests.invalidation.confirm")}
        </Button>,
        <Button
          id="invalidation-modal-cancel"
          key="cancel"
          variant="outlined"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {t("pickupRequests.invalidation.cancel")}
        </Button>,
      ]}
    >
      <TextInput
        isMultiline
        required
        id="invalidation-modal-reasons"
        title={t("pickupRequests.invalidation.reasonsLabel")}
        value={comment}
        maxLength={MAX_COMMENT_LENGTH}
        disabled={isSubmitting}
        isError={isReasonsMissing}
        errorText={t("pickupRequests.invalidation.reasonsRequired")}
        onChange={handleCommentChange}
        wrapperClassName={styles.reasonsField}
      />
      {showParticipantsOption && (
        <Checkbox
          id="invalidation-modal-show-to-participants"
          title={t("pickupRequests.invalidation.showToParticipantsLabel")}
          value={showToParticipants}
          disabled={isSubmitting}
          onChange={(_event, checked) => setShowToParticipants(checked)}
        />
      )}
    </Dialog>
  );
};

export default InvalidationModal;
